/**
 * Single-pass snapshot for live map overlays (wind, bad-air data) from one getFilteredData call.
 */

export interface LiveWindSnapshot {
  tws: number | undefined;
  twd: number | undefined;
}

export interface LiveFrameSnapshot {
  filteredMap: Map<number, any[]>;
  wind: LiveWindSnapshot;
  overlayData: any[];
}

function readFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function readWindFromPoints(
  points: any[],
  timeRef: Date | null,
  twdField: string,
  twsField: string
): LiveWindSnapshot {
  if (!points.length) return { tws: undefined, twd: undefined };

  let filteredData = points;
  if (timeRef) {
    const windowEnd = timeRef;
    const windowStart = new Date(timeRef.getTime() - 10000);
    filteredData = points.filter((point: any) => {
      const pt = point.Datetime;
      if (!pt) return false;
      const d = pt instanceof Date ? pt : new Date(pt);
      return d >= windowStart && d <= windowEnd;
    });
    if (filteredData.length === 0) {
      filteredData = points.filter((point: any) => {
        const pt = point.Datetime;
        if (!pt) return false;
        const d = pt instanceof Date ? pt : new Date(pt);
        return d <= timeRef;
      });
    }
  }

  const twdValues: number[] = [];
  const twsValues: number[] = [];
  for (const point of filteredData) {
    const twd =
      point[twdField] ??
      point[twdField?.toLowerCase()] ??
      point.Twd ??
      point.twd ??
      point.TWD;
    const tws =
      point[twsField] ??
      point[twsField?.toLowerCase()] ??
      point.Tws ??
      point.tws ??
      point.TWS;
    const twdNum = readFiniteNumber(twd);
    const twsNum = readFiniteNumber(tws);
    if (twdNum !== undefined) twdValues.push(twdNum);
    if (twsNum !== undefined) twsValues.push(twsNum);
  }
  if (!twdValues.length || !twsValues.length) return { tws: undefined, twd: undefined };

  const sinSum = twdValues.reduce((s, a) => s + Math.sin(a * (Math.PI / 180)), 0);
  const cosSum = twdValues.reduce((s, a) => s + Math.cos(a * (Math.PI / 180)), 0);
  const meanAngle = Math.atan2(sinSum, cosSum) * (180 / Math.PI);
  const meanTWD = ((meanAngle % 360) + 360) % 360;
  const meanTWS = twsValues.reduce((s, v) => s + v, 0) / twsValues.length;
  return { tws: meanTWS, twd: meanTWD };
}

export function buildLiveFrameSnapshot(
  filteredMap: Map<number, any[]>,
  effectivePlaybackTime: Date | null,
  selected: Set<number>,
  channelNames: { latName: string; lngName: string; twsName: string; twdName: string }
): LiveFrameSnapshot {
  const windPoints: any[] = [];
  let overlayFlat: any[] = [];

  for (const pts of filteredMap.values()) {
    const recentWind = pts.length > 40 ? pts.slice(-40) : pts;
    windPoints.push(...recentWind);
    const slice = pts.length > 400 ? pts.slice(-400) : pts;
    overlayFlat.push(...slice);
  }

  const timeRef = effectivePlaybackTime;
  const wind = readWindFromPoints(
    windPoints,
    timeRef,
    channelNames.twdName,
    channelNames.twsName
  );

  const sourceKey = (d: any) => d?.source_id ?? d?.Source_id ?? d?.sourceId ?? d?.sourceID;
  const { latName, lngName } = channelNames;

  overlayFlat = overlayFlat.filter((d) => {
    const sid = Number(sourceKey(d));
    return Number.isFinite(sid) && selected.has(sid);
  });
  overlayFlat = overlayFlat.filter((d) => {
    const lng = d[lngName] ?? d.Lng_dd;
    const lat = d[latName] ?? d.Lat_dd;
    return Number.isFinite(lng) && Number.isFinite(lat);
  });
  overlayFlat.sort((a, b) => {
    const ta = new Date(a.Datetime || a.timestamp || 0).getTime();
    const tb = new Date(b.Datetime || b.timestamp || 0).getTime();
    return ta - tb;
  });

  return { filteredMap, wind, overlayData: overlayFlat };
}
